import {
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Heading,
  Spinner,
  Text,
} from '@chakra-ui/react';
import analytics from 'services/analytics/analytics';
import { usePage as useAnalyticsPage } from 'services/analytics/hooks';
import { INTAKE_PAYMENT_COMPLETE } from 'services/analytics/events';
import IntakeConfig from '../../../interfaces/IntakeConfig.interface';
import { BusinessModel } from '../../../interfaces/BusinessModel.interface';
import validateConfig from '../validateConfig';
import BusinessView from './BusinessView';
import ChargebeeView from '../ChargebeeView';
import CompleteView from './SocialCompleteView';
import AyrshareView from '../AyrshareView';
import extractErrorDetails from '../extractErrorDetails';
import useChargebeeCompleteCallback from '../useChargebeeCompleteCallback';
import useBusinessCompleteCallback from '../useBusinessCompleteCallback';
import usePage from '../../../hooks/usePage';
import rollbar from '../../../services/intake/rollbar';
import ExternalLink from '../ExternalLink';
import LoadingScreen from 'components/Intake/LoadingScreen';

const IntakeViewModel = ({ config }: { config: IntakeConfig }) => {
  const isCheckoutCompleteRef = useRef(false);
  const stepsCount = config.steps ? Object.keys(config.steps).length : 3;
  const [page, next, /* decrement */, jump] = usePage(
    0,
    stepsCount,
  );

  useAnalyticsPage({ title: config.partner.name });

  const [business, setBusiness] = useState<BusinessModel>();

  useEffect(
    () => {
      // If we've redirected from Social linking, jump to "Complete" step
      const isComplete = new URL(window.location.href).searchParams.get('step') === 'complete';
      if (isComplete) {
        rollbar.info('Redirect From Ayrshare', business);
        jump(stepsCount - 1);
      }
    },
    [],
  );

  useEffect(() => validateConfig(config), []);

  const [onBusinessComplete, onBusinessCompleteLoading, onBusinessCompleteError] = useBusinessCompleteCallback(
    config,
    (newBusiness: BusinessModel) => {
      setBusiness(newBusiness);
      next();
    },
  );

  const [onChargebeeComplete, onChargebeeCompleteLoading, onChargebeeCompleteError] = useChargebeeCompleteCallback(
    business,
    () => {
      // Somehow, the Chargebee widget is executing the callback twice
      if (isCheckoutCompleteRef.current) return;
      isCheckoutCompleteRef.current = true;
      analytics()?.track(INTAKE_PAYMENT_COMPLETE);
      rollbar.info('Partner Checkout Complete', business);
      next();
    },
  );

  const loading = onBusinessCompleteLoading || onChargebeeCompleteLoading;
  const error = onBusinessCompleteError || onChargebeeCompleteError;
  const errorDetails = extractErrorDetails(error);

  const onAyrshareComplete = useCallback(
    () => {
      next();
    },
    [],
  );

  return (
    <>
      <Heading fontWeight={800}>{config.pageTitle || 'Business Partner Intake Form'}</Heading>

      <LoadingScreen isLoading={loading}>
        <Spinner />
        <Text textAlign="center" sx={{ textWrap: 'balance' }}>
          We're completing the partner upload, this should only take a minute.
        </Text>
        <Text textAlign="center">Please do not refresh...</Text>
      </LoadingScreen>
      {page === 0 && <BusinessView config={config} onComplete={onBusinessComplete} />}
      {page === 1 && business && <ChargebeeView business={business} onComplete={onChargebeeComplete} />}
      {page === 2 && business && <AyrshareView onComplete={onAyrshareComplete} business={business} />}
      {page >= 3 && <CompleteView />}
      {error && (
        <Alert status="error" flexDirection="column" gap="1em">
          <AlertIcon />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>
            {/* eslint-disable-next-line max-len */}
            Please try again or contact <ExternalLink href="mailto:support@getintheloop.ca">support@getintheloop.ca</ExternalLink>.
          </AlertDescription>
          <AlertDescription>
            {errorDetails}
          </AlertDescription>
        </Alert>
      )}
    </>
  );
};

export default IntakeViewModel;
